import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  Crown,
  ArrowLeft,
  Sparkles,
  Tag,
  ShieldCheck,
  Copy,
  Check,
  ExternalLink,
  CheckCircle2,
  Clock,
  XCircle,
  HelpCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";

const PLAN_LABELS: Record<string, { label: string; period: string }> = {
  monthly: { label: "Monthly", period: "/ month" },
  yearly: { label: "Yearly", period: "/ year" },
  lifetime: { label: "Lifetime", period: "one-time" },
};

function fmtEur(n: number, currency: string = "EUR") {
  if (n <= 0) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
  }).format(n);
}

function normalizePlan(p: string | null): string {
  const x = (p || "monthly").toLowerCase();
  return x in PLAN_LABELS ? x : "monthly";
}

function CopyBtn({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 text-[11px] px-2"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast.success("Copied.");
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Copy failed.");
        }
      }}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {label || (copied ? "OK" : "Copy")}
    </Button>
  );
}

export function PremiumCheckout() {
  const { guildId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const plan = normalizePlan(searchParams.get("plan"));

  const { data: guildsData } = useQuery({
    queryKey: ["guilds"],
    queryFn: () => api.guilds(),
  });

  const { data: me } = useQuery<any>({
    queryKey: ["me"],
    queryFn: api.me,
  });

  const { data: info, isLoading, refetch: refetchInfo } = useQuery({
    queryKey: ["premium-info", guildId],
    queryFn: () => api.premiumInfo(guildId!),
    enabled: !!guildId,
  });

  const { data: myReqs, refetch: refetchMy } = useQuery({
    queryKey: ["my-premium-requests"],
    queryFn: () => api.myPremiumRequests(),
    refetchInterval: 30_000,
  });

  const guildName =
    guildsData?.guilds.find((g) => g.id === guildId)?.name ?? "Server";

  const [promoInput, setPromoInput] = useState("");
  const [debouncedPromo, setDebouncedPromo] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPromo(promoInput.trim()), 400);
    return () => clearTimeout(t);
  }, [promoInput]);

  const { data: promoPreview } = useQuery({
    queryKey: ["promo-preview", debouncedPromo, plan],
    queryFn: () => api.promoPreview(debouncedPromo, plan),
    enabled: debouncedPromo.length >= 2,
  });

  // Manual payment state
  const [selectedMethod, setSelectedMethod] = useState<
    "paypal_me" | "revolut" | "iban" | "other"
  >("paypal_me");
  const [paymentRef, setPaymentRef] = useState("");
  const [note, setNote] = useState("");

  const paypalMut = useMutation({
    mutationFn: () =>
      api.paypalCreateCheckout(
        guildId!,
        plan,
        promoInput.trim() || undefined,
      ),
    onSuccess: (resp) => {
      if (resp.approve_url) {
        toast.success("Redirecting to PayPal…");
        window.location.href = resp.approve_url;
      } else {
        toast.error("PayPal did not return a checkout link.");
      }
    },
    onError: (e: Error) =>
      toast.error(e.message || "Could not create PayPal payment"),
  });

  const manualMut = useMutation({
    mutationFn: () =>
      api.requestManualPremium({
        guild_id: guildId!,
        plan,
        payment_method: selectedMethod,
        payment_reference: paymentRef,
        note,
        promo_code: promoInput.trim() || undefined,
      }),
    onSuccess: (resp) => {
      toast.success(resp.message || "Request submitted!");
      setPaymentRef("");
      setNote("");
      refetchMy();
      refetchInfo();
    },
    onError: (e: Error) => toast.error(e.message || "Error"),
  });

  const simulateMut = useMutation({
    mutationFn: () =>
      api.simulatePremiumCheckout(
        guildId!,
        plan,
        promoInput.trim() || undefined,
      ),
    onSuccess: (resp) => {
      toast.success("Premium activated (demo).");
      navigate(resp.redirect || `/server/${guildId}`);
    },
    onError: (e: Error) => toast.error(e.message || "Error"),
  });

  if (!guildId) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Missing server.</p>
        <Button className="mt-4" asChild variant="outline">
          <Link to="/premium">Back</Link>
        </Button>
      </Card>
    );
  }

  if (isLoading || !info) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!info.can_buy) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <Toaster position="top-right" theme="dark" richColors />
        <p className="text-destructive">{info.reason}</p>
        <Button asChild variant="outline">
          <Link to="/dashboard">Back</Link>
        </Button>
      </div>
    );
  }

  const currency = info.currency || "EUR";
  const priceMap = info.prices || { monthly: 5, yearly: 45, lifetime: 100 };
  const pu = PLAN_LABELS[plan];
  const baseNum = priceMap[plan] ?? 0;
  const pct = promoPreview?.valid ? promoPreview.percent_off : 0;
  const finalNum =
    baseNum > 0 && pct > 0
      ? Math.round(baseNum * (1 - pct / 100) * 100) / 100
      : baseNum;
  const isRecurring = plan === "monthly" || plan === "yearly";
  const paypalAvailable = !!info.paypal_configured;
  const manualCfg = info.manual_payment;
  const manualAvailable = !!manualCfg?.enabled && manualCfg.methods.length > 0;
  const showDemoButton = !!info.allow_simulate;

  const activeMethodCfg = manualCfg?.methods.find((m) => m.id === selectedMethod);
  const amountForUrl = finalNum.toFixed(2);

  const LS_PLANS: Record<string, string> = {
    monthly: "https://orbit-premium.lemonsqueezy.com/buy/41eb3d46-784d-44a4-a146-8c8025803c37",
    yearly: "https://orbit-premium.lemonsqueezy.com/buy/57d18d17-4434-4c5a-b5a8-68b76fa55095",
    lifetime: "https://orbit-premium.lemonsqueezy.com/buy/e75741b0-8739-41f9-8265-b2c79c71cb25",
  };

  const getCheckoutUrl = () => {
    const baseUrl = LS_PLANS[plan] || LS_PLANS.monthly;
    let url = baseUrl;
    const params = [];
    if (me?.id) params.push(`checkout[custom][discord_user_id]=${me.id}`);
    if (guildId) params.push(`checkout[custom][discord_guild_id]=${guildId}`);
    params.push(`checkout[custom][plan]=${plan}`);
    if (promoPreview?.valid && promoInput.trim()) {
      params.push(`checkout[discount_code]=${encodeURIComponent(promoInput.trim())}`);
    }
    if (params.length) {
      url += `?${params.join("&")}`;
    }
    return url;
  };

  // This user’s requests for this guild + plan
  const relevantReqs = (myReqs?.requests || []).filter(
    (r) => r.guild_id === guildId && r.plan === plan,
  );
  const pendingReq = relevantReqs.find((r) => r.status === "pending");

  return (
    <div className="max-w-lg mx-auto w-full min-w-0 space-y-6 py-2 sm:py-4 px-0">
      <Toaster position="top-right" theme="dark" richColors />

      <Button variant="ghost" size="sm" asChild>
        <Link to={`/premium/${guildId}`}>
          <ArrowLeft className="h-4 w-4" />
          Back to plans
        </Link>
      </Button>

      <Card className="p-4 sm:p-6 space-y-5 border-amber-500/20">
        <div className="text-center space-y-2">
          <Badge variant="premium" className="inline-flex">
            <Crown className="h-3 w-3" />
            Checkout
          </Badge>
          <h1 className="text-2xl font-bold">Premium checkout</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{guildName}</span>
            <span className="mx-1">·</span>
            <code className="text-xs break-all max-w-full inline-block">
              {guildId}
            </code>
          </p>
        </div>

        <div className="rounded-lg border border-border bg-muted/20 p-4 text-center space-y-1">
          <div className="text-sm text-muted-foreground">{pu.label}</div>
          {baseNum > 0 && pct > 0 ? (
            <>
              <div className="text-sm text-muted-foreground">
                <s>{fmtEur(baseNum, currency)}</s>{" "}
                <Badge variant="secondary" className="ml-1">
                  −{pct}%
                </Badge>
              </div>
              <div className="text-3xl font-bold text-gradient-gold">
                {fmtEur(finalNum, currency)}
                <span className="text-base font-normal text-muted-foreground">
                  {" "}
                  {pu.period}
                </span>
              </div>
            </>
          ) : (
            <div className="text-3xl font-bold text-gradient-gold">
              {fmtEur(baseNum, currency)}
              <span className="text-base font-normal text-muted-foreground">
                {" "}
                {pu.period}
              </span>
            </div>
          )}
          {isRecurring ? (
            <p className="text-[11px] text-muted-foreground pt-1">
              Pay once; Premium is active for {plan === "yearly" ? "365" : "30"} days.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground pt-1">
              One-time payment; Premium is lifetime.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Tag className="h-3.5 w-3.5" />
            Promo code (optional)
          </Label>
          <Input
            placeholder="e.g. SUMMER26"
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
            className="font-mono uppercase"
          />
          {debouncedPromo.length >= 2 && promoPreview ? (
            <p
              className={`text-xs ${promoPreview.valid ? "text-amber-400" : "text-destructive"}`}
            >
              {promoPreview.valid
                ? `Code valid: ${promoPreview.percent_off}% off.`
                : promoPreview.message}
            </p>
          ) : null}
        </div>

        {/* ========== PENDING REQUEST ========== */}
        {pendingReq ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-400">
              <Clock className="h-4 w-4" />
              <span className="font-semibold text-sm">
                Request in review
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              You submitted request{" "}
              <code className="text-[10px]">#{pendingReq.id}</code>. An admin will
              verify your payment and activate Premium within 24h. You can submit
              another request below if needed.
            </p>
          </div>
        ) : null}

        {/* ========== LEMON SQUEEZY (AUTOMATIC) ========== */}
        <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-primary" />
            <h2 className="font-semibold text-sm">Automatic Payment (Lemon Squeezy)</h2>
            <Badge variant="premium" className="text-[10px] ml-auto">
              Instant Activation
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Pay securely using Credit Card, Apple Pay, or Google Pay via Lemon Squeezy. Premium will be activated on your server **instantly after checkout**.
          </p>
          <Button
            className="w-full h-11 text-sm bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
            onClick={() => window.open(getCheckoutUrl(), "_blank")}
          >
            Pay via Lemon Squeezy ({fmtEur(finalNum, currency)})
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* ========== DISCORD SUPPORT TICKET (MANUAL) ========== */}
        <div className="space-y-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 text-amber-400">
            <HelpCircle className="h-4 w-4 text-amber-500" />
            <h2 className="font-semibold text-sm text-amber-400">Manual Payment (Discord Ticket)</h2>
            <Badge variant="secondary" className="text-[10px] ml-auto">
              Processed within 24h
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Want to pay via **Revolut, Bank Transfer (SEPA/IBAN), Cash, or Cryptocurrencies**? 
            Open a support ticket on our Discord and we will gladly activate your Premium manually.
          </p>
          <Button
            className="w-full h-11 text-sm bg-amber-500 hover:bg-amber-600 text-black font-bold"
            onClick={() => window.open((info as any).support_server_url || "https://discord.gg/8eh7KrMPXg", "_blank")}
          >
            Open Support Ticket on Discord
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
          
          <div className="pt-2 border-t border-border space-y-2">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              If you have already made the payment and want to submit a verification request, you can enter your transaction reference below (e.g. "Revolut transfer", "Bank transfer"):
            </p>
            <Input
              placeholder="e.g. Revolut transaction ID, Bank reference number, etc."
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              maxLength={300}
              className="h-8 text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
              onClick={() => manualMut.mutate()}
              disabled={manualMut.isPending || paymentRef.trim().length < 3}
            >
              Submit Manual Request
            </Button>
          </div>
        </div>

        {showDemoButton ? (
          <div className="pt-4 border-t border-border">
            <Button
              className="w-full"
              variant="outline"
              size="sm"
              onClick={() => simulateMut.mutate()}
              disabled={simulateMut.isPending}
            >
              <Sparkles className="h-4 w-4" />
              {simulateMut.isPending
                ? "Activating…"
                : "Demo activation (no charge)"}
            </Button>
            <p className="text-center text-[10px] text-muted-foreground mt-1">
              Test mode. In production set{" "}
              <code>ALLOW_PREMIUM_SIMULATION=0</code>.
            </p>
          </div>
        ) : null}

        <Button variant="ghost" className="w-full" asChild>
          <Link to="/dashboard">Cancel</Link>
        </Button>
      </Card>

      {/* Request history */}
      {myReqs && myReqs.requests.length > 0 ? (
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold">My requests</h3>
          <div className="space-y-2">
            {myReqs.requests.slice(0, 5).map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-3 text-xs rounded-md border border-border bg-muted/20 p-2"
              >
                {r.status === "pending" ? (
                  <Clock className="h-4 w-4 text-amber-400 flex-shrink-0" />
                ) : r.status === "approved" ? (
                  <CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-[10px]">#{r.id}</code>
                    <span className="font-medium">
                      {PLAN_LABELS[r.plan]?.label || r.plan}
                    </span>
                    <Badge
                      variant={
                        r.status === "approved"
                          ? "secondary"
                          : r.status === "pending"
                            ? "premium"
                            : "destructive"
                      }
                      className="text-[9px]"
                    >
                      {r.status}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {r.payment_method} · {r.payment_reference}
                  </div>
                  {r.reject_reason ? (
                    <div className="text-[10px] text-destructive mt-0.5">
                      Reason: {r.reject_reason}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
